import { useCallback, useEffect, useMemo } from 'react';
import { LoadingSpinner } from '@figma/fpl-components';
import { useTranslation } from 'react-i18next';
import { ErrorDisplay } from '@/components/Common/Error/ErrorDisplay';
import { useGenerateModelAccess } from '@/components/Generate/generateModelAccess';
import { isInternalUser } from '@/hooks/useDebugContext';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useCatalogToolsQuery } from '@/queries/useCatalogTools';
import { useGenerateModelsQuery } from '@/queries/useGeneratePage';
import { useUserStore } from '@/state/user.state';
import { FlexColCenHorVer } from '@/UI/styles';
import { TOOLKIT_CATALOG } from './toolkitCatalog';
import { linkedToolIds, resolveToolkitCatalog } from './resolveToolkitCatalog';
import { ToolsCatalog } from './ToolsCatalog';
import type { GeneratorModelSlug } from '@/consts/generatorModelSlugs';

const TOOL_IDS = linkedToolIds(TOOLKIT_CATALOG);

/**
 * The Toolkit with its tool cards filled from their system tools, and only the model cards the generator
 * offers this person. The whole page waits for both, so rows do not change size while cards arrive.
 */
export function Toolkit() {
  const { t } = useTranslation();
  const tools = useCatalogToolsQuery(TOOL_IDS);
  const showMissing = isInternalUser(useUserStore((state) => state.user?.email));
  // Also what the generator's model menu loads, so the generator opens without waiting for it.
  const generatorModels = useGenerateModelsQuery(true);
  const { accessOf, status: modelAccessStatus } = useGenerateModelAccess(true);
  // Until both are in, the list holds models whose definition may be missing, and no admin allow-list.
  const isModelListPending =
    (generatorModels.isPlaceholderData && !generatorModels.isError) || modelAccessStatus === 'loading';

  // A card only for a model the generator would open ready to use. A model it lists as locked opens as
  // "not available" from a link, and so does every model when either list failed to load.
  const isModelOffered = useCallback(
    (modelId: GeneratorModelSlug) => {
      if (generatorModels.isPlaceholderData || modelAccessStatus !== 'ready') {
        return false;
      }
      const model = generatorModels.data?.find((option) => option.slug === modelId);
      return model !== undefined && accessOf(model) === 'available';
    },
    [accessOf, generatorModels.data, generatorModels.isPlaceholderData, modelAccessStatus],
  );

  // A missing tool is logged by the server with its reason. A failed request may never reach the server.
  useEffect(() => {
    if (tools.error) {
      log.error('Toolkit could not load its system tools', tools.error);
    }
  }, [tools.error]);

  const catalog = useMemo(
    () => (tools.data ? resolveToolkitCatalog(TOOLKIT_CATALOG, tools.data, showMissing, isModelOffered) : null),
    [tools.data, showMissing, isModelOffered],
  );

  // Not PageLoader: it covers the whole dashboard, so the sidebar would be hidden while this loads.
  if (tools.isPending || isModelListPending) {
    return (
      <FlexColCenHorVer sx={{ width: '100%', height: '100%' }}>
        <LoadingSpinner size="lg" />
      </FlexColCenHorVer>
    );
  }
  // Only when there is nothing to show. A failed reload keeps the last catalog on screen.
  if (!catalog) {
    return (
      <FlexColCenHorVer sx={{ width: '100%', height: '100%' }}>
        <ErrorDisplay
          title={t(I18N_KEYS.TOOLKIT.LOAD_FAILED)}
          description={null}
          actions={[{ label: t(I18N_KEYS.ERROR_PAGE.RETRY_ACTION), onClick: () => void tools.refetch() }]}
        />
      </FlexColCenHorVer>
    );
  }
  return <ToolsCatalog catalog={catalog} />;
}
